import React from 'react';
import PropTypes from 'prop-types';
import { LuPlay } from 'react-icons/lu';

/** Round icon button on the dark video control bar. */
export const VideoButton = ({ label, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    aria-label={label}
    className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-full tw-text-white/85 tw-transition-colors hover:tw-bg-white/15 hover:tw-text-white focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-400/70"
  >
    {children}
  </button>
);

VideoButton.propTypes = { label: PropTypes.string.isRequired, onClick: PropTypes.func.isRequired, children: PropTypes.node };

/** Big play button over a paused video. */
export const PlayOverlay = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="tw-group tw-absolute tw-inset-0 tw-grid tw-place-items-center tw-bg-black/30 focus-visible:tw-outline-none"
  >
    <span className="tw-grid tw-h-16 tw-w-16 tw-place-items-center tw-rounded-full tw-bg-white/95 tw-text-teal-600 tw-shadow-2xl tw-transition-transform group-hover:tw-scale-110 group-focus-visible:tw-ring-4 group-focus-visible:tw-ring-teal-400/60">
      <LuPlay aria-hidden="true" className="tw-ml-1 tw-h-7 tw-w-7" />
    </span>
  </button>
);

PlayOverlay.propTypes = { label: PropTypes.string.isRequired, onClick: PropTypes.func.isRequired };

/** Volume button and slider. */
export const VolumeControl = ({ muted, volume, onToggleMute, onChange, muteLabel, unmuteLabel, volumeLabel, icon: Icon, mutedIcon: MutedIcon }) => (
  <div className="tw-group tw-flex tw-items-center">
    <VideoButton label={muted ? unmuteLabel : muteLabel} onClick={onToggleMute}>
      {muted ? <MutedIcon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /> : <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />}
    </VideoButton>
    <input
      type="range"
      min="0"
      max="1"
      step="0.05"
      value={muted ? 0 : volume}
      onChange={onChange}
      aria-label={volumeLabel}
      className="tw-w-0 tw-cursor-pointer tw-accent-teal-400 tw-opacity-0 tw-transition-all tw-duration-200 group-hover:tw-ml-1 group-hover:tw-w-20 group-hover:tw-opacity-100 focus:tw-ml-1 focus:tw-w-20 focus:tw-opacity-100 max-sm:tw-hidden"
    />
  </div>
);

VolumeControl.propTypes = {
  muted: PropTypes.bool,
  volume: PropTypes.number,
  onToggleMute: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  muteLabel: PropTypes.string.isRequired,
  unmuteLabel: PropTypes.string.isRequired,
  volumeLabel: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  mutedIcon: PropTypes.elementType.isRequired,
};

/** Learning progress (0–100) with its label. */
export const LearningProgress = ({ label, percent, valueText }) => (
  <div className="tw-space-y-1.5">
    <div className="tw-flex tw-items-center tw-justify-between tw-text-xs tw-font-semibold">
      <span className="tw-text-slate-500 dark:tw-text-slate-400">{label}</span>
      <span className="tw-tabular-nums tw-text-teal-700 dark:tw-text-teal-300">{valueText}</span>
    </div>
    <span className="tw-block tw-h-2 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
      <span className="tw-block tw-h-full tw-rounded-full tw-bg-gradient-to-r tw-from-teal-400 tw-to-cyan-500 tw-transition-[width] tw-duration-500" style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
    </span>
  </div>
);

LearningProgress.propTypes = { label: PropTypes.string.isRequired, percent: PropTypes.number.isRequired, valueText: PropTypes.string.isRequired };

// mm:ss
export const formatTime = (timeInSeconds) => {
  if (Number.isNaN(timeInSeconds) || timeInSeconds == null) return '0:00';
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};
